import { useCallback, useEffect, useState } from 'react';
import { Group, Model, Prompt, PromptFunction, User } from '../Types';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useGlobals from './useGlobals';
import { groupService } from '../Services/groupService';
import { userService } from '../Services/userService';

const useGroup = () => {
    const { userSettings, i18n, demo, handleNavigate } = useGlobals();
    const { groupIdentifier = '' } = useParams() || '';
    const [group, setGroup] = useState({} as Group);
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [selectedPromptIdentifiers, setSelectedPromptIdentifiers] = useState<string[]>([]);
    const [maxCosts, setMaxCosts] = useState<number>(0.0);
    const [title, setTitle] = useState<string>(group.title);
    const [selectedModelIdentifiers, setSelectedModelIdentifiers] = useState<string[]>([]);
    const [models, setModels] = useState<Model[]>([]);
    const [functions, setFunctions] = useState<PromptFunction[]>([]);
    const [selectedFunctionIdentifiers, setSelectedFunctionIdentifiers] = useState<string[]>([]);
    const [groupLoaded, setGroupLoaded] = useState<boolean>(false);
    const [users] = useState<User[]>([]);
    const [usersLoaded, setUsersLoaded] = useState<boolean>(false);

    const notifySuccess = useCallback(() => {
        toast.success(i18n.translate({ id: 'saveGroup', sourceName: 'Group' }));
    }, [i18n]);

    const getUsersByGroupWrapper = (page: number, itemsPerPage: number, optionalParams?: Record<string, string>) => {
        return userService.getUsersByGroup(page, itemsPerPage, groupIdentifier);
    };

    const handleSave = useCallback(() => {
        if (demo) {
            notifySuccess();
            return;
        }

        if (!userSettings.isLoggedIn) return;

        const data = new URLSearchParams();
        data.append('title', title);
        data.append('maxCosts', maxCosts.toString());
        selectedPromptIdentifiers.forEach((prompt) => {
            data.append('prompts[]', prompt);
        });
        selectedModelIdentifiers.forEach((model) => {
            data.append('models[]', model);
        });
        selectedFunctionIdentifiers.forEach((func) => {
            data.append('functions[]', func);
        });

        let type = 'update/' + groupIdentifier;
        if (groupIdentifier === 'new') {
            type = 'create';
        }

        groupService.saveGroup(type, data).then((response) => {
            setGroup(response.data);
            notifySuccess();
            groupIdentifier === 'new' && handleNavigate('/admin/groups/' + response.data.identifier);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }, [
        demo,
        groupIdentifier,
        maxCosts,
        selectedModelIdentifiers,
        selectedPromptIdentifiers,
        selectedFunctionIdentifiers,
        title,
        userSettings,
        notifySuccess,
        handleNavigate,
    ]);

    const handleMaxCostChange = useCallback((value: number) => {
        const newMaxCosts = value;
        setMaxCosts(newMaxCosts);
    }, []);

    const handleTitleChange = useCallback((value: string) => {
        setTitle(value);
    }, []);

    const handlePromptCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSelectedPromptIdentifiers = event.target.checked
                ? [...selectedPromptIdentifiers, event.target.name]
                : selectedPromptIdentifiers.filter((model) => model !== event.target.name);

            setSelectedPromptIdentifiers(newSelectedPromptIdentifiers);
        },
        [selectedPromptIdentifiers],
    );

    const handleModelCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSelectedModelIdentifiers = event.target.checked
                ? [...selectedModelIdentifiers, event.target.name]
                : selectedModelIdentifiers.filter((model) => model !== event.target.name);

            setSelectedModelIdentifiers(newSelectedModelIdentifiers);
        },
        [selectedModelIdentifiers],
    );

    const handleFunctionCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newSelectedFunctionIdentifiers = event.target.checked
                ? [...selectedFunctionIdentifiers, event.target.name]
                : selectedFunctionIdentifiers.filter((func) => func !== event.target.name);

            setSelectedFunctionIdentifiers(newSelectedFunctionIdentifiers);
        },
        [selectedFunctionIdentifiers],
    );

    useEffect(() => {
        if (!userSettings.isLoggedIn) return;

        const fetchData = async () => {
            try {
                const [groupResponse, promptsResponse, modelsResponse, functionsResponse] = await Promise.all([
                    groupIdentifier !== 'new' ? groupService.getGroup(groupIdentifier) : null,
                    groupService.getPrompts(),
                    groupService.getModels(),
                    groupService.getFunctions(),
                ]);

                if (groupResponse) {
                    setGroup(groupResponse.data);
                    setTitle(groupResponse.data.title);
                    setMaxCosts(parseFloat(groupResponse.data.maxCosts));
                    setSelectedPromptIdentifiers(groupResponse.data.prompts);
                    setSelectedModelIdentifiers(groupResponse.data.models);
                    setSelectedFunctionIdentifiers(groupResponse.data.functions);
                }

                setPrompts(promptsResponse.data);
                setModels(modelsResponse.data);
                setFunctions(functionsResponse.data);
                setGroupLoaded(true);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        const fetchUsers = async () => {
            setUsersLoaded(true);
        };

        fetchData();
        fetchUsers();
    }, [demo, groupIdentifier, userSettings.isLoggedIn]);

    return {
        GroupActions: {
            handleSave,
            handleMaxCostChange,
            handleTitleChange,
            handlePromptCheckboxChange,
            handleModelCheckboxChange,
            handleFunctionCheckboxChange,
            getUsersByGroupWrapper,
        },
        groupIdentifier,
        group,
        prompts,
        selectedPromptIdentifiers,
        maxCosts,
        title,
        selectedModelIdentifiers,
        models,
        functions,
        selectedFunctionIdentifiers,
        groupLoaded,
        i18n,
        usersLoaded,
        users,
        demo,
        handleNavigate,
    };
};

export default useGroup;
